import {useContext, useEffect, useRef, useState} from "react";
import {Box, Typography} from "@mui/material";
import {useNavigate, useSearchParams} from "react-router-dom";
import axios from "../../config/axios";
import aiFlowBuilder from "../../config/axios-ai-flow-builder";
import {REDIRECT_MESSAGES_BY_LANGUAGE} from "./redirectMessages";
import {TranscribeModelRepository} from "../../repositories/TranscribeModelRepository";
import {createLanguageConfig, toApiConfiguration} from "../project-creation/components/LanguageConfigurationsNew";
import {BackendConfigurationRepository} from "../../repositories/BackendConfigurationRepository";
import {KnowledgeManagerRepository} from "../../repositories/KnowledgeManagerRepository";
import {LlmModelRepository} from "../../repositories/LlmModelRepository";
import {OrganizationRepository} from "../../repositories/OrganizationRepository";
import {ProjectRepository, SubProjectRepository} from "../../repositories/ProjectRepository";
import {deleteDoc, doc} from "firebase/firestore";
import {db} from "../../config/firebase";
import {PROJECTS_COLLECTION} from "../../db/collections";
import {SignInContext} from "../../context/SignInContext";
import {Context as AppContext, ProjectRoles} from "../../context/AppContext";
import {NodeType, ProjectEntity} from "../../model/ModelData";
import {v4 as uuid} from "uuid";
import "./onboarding.css";

const MAX_KB_CHARS = 5000;
const DEFAULT_LLM_MODEL_ID = "gpt-6-luna";

const STEPS = [
    "Creating your account.",
    "Creating your organization.",
    "Preparing your project.",
    "Scanning your website.",
    "Saving your website knowledge.",
    "Generating your agent flow.",
    "Saving your generated flow.",
    "Opening your flow editor.",
];

const STATUS_MESSAGES = [
    "Securely preparing your workspace.",
    "Setting up your organization.",
    "Saving the first version of your project.",
    "Reading your website to learn about your business.",
    "Adding what we learned to your knowledge base.",
    "Your prompt is being turned into a working flow. This can take a moment.",
    "Applying the generated flow to your project.",
    "Everything is ready. Opening the flow editor now.",
];
const GENERATION_STEP = STEPS.indexOf("Generating your agent flow.");
const GENERATION_MESSAGES = [
    "Your prompt is being turned into a working flow. This can take a few minutes.",
    "Mapping out the conversation paths for your agent.",
    "Writing the messages your agent will say.",
    "Connecting the steps of the conversation together.",
    "Good flows take a moment. Thanks for your patience.",
    "Still working. Bigger businesses take a little longer.",
];
const GENERATION_MESSAGE_INTERVAL_MS = 6000;
const FLOW_POLL_INTERVAL_MS = 2500;
const FLOW_POLL_TIMEOUT_MS = 10 * 60 * 1000;

// create-flow is a background job: start it, then poll until it is done or failed
async function generateFlow(payload) {
    const {job_id: jobId} = (await aiFlowBuilder.post("/api/create-flow", payload)).data;
    const deadline = Date.now() + FLOW_POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, FLOW_POLL_INTERVAL_MS));
        const job = (await aiFlowBuilder.get(`/api/create-flow/${jobId}`)).data;
        if (job.status === "done") return job.flow;
        if (job.status === "error") throw new Error(job.error || "Flow generation failed.");
    }
    throw new Error("Flow generation timed out.");
}
const FRIENDLY_ERROR_MESSAGES = {
    setup: "We couldn't complete the setup. Please try again.",
    organization: "We couldn't create your organization. Please try again.",
    project: "We couldn't create your project. Please try again.",
    generation: "We couldn't generate your AI agent. Please try again.",
    save: "We couldn't save your generated flow. Please try again.",
    publish: "We couldn't publish your flow. Please try again.",
};

const fetchSiteKnowledge = async (siteUrl) => {
    const url = siteUrl?.trim();
    if (!url) return "";
    try {
        const response = await aiFlowBuilder.post("/api/scrape", {url, mode: "auto", use_cache: true});
        return (response.data?.markdown || "").trim();
    } catch {
        return "";
    }
};

const saveSiteKnowledge = async ({siteUrl, markdown, organizationId, projectId, subProjectId}) => {
    try {
        const hostname = new URL(siteUrl).hostname;
        const topic = await KnowledgeManagerRepository.createTopic({
            name: `${hostname}-${projectId}`,
            organization: organizationId,
            projectId: String(projectId),
            subProjectId: String(subProjectId),
        });
        await KnowledgeManagerRepository.uploadTextDocument(topic.id, {content: markdown, fileName: `${hostname}.md`});
    } catch (e) {
        console.error("Saving site knowledge failed:", e);
    }
};
const collectFlowIds = (value, ids = []) => {
    if (!value || typeof value !== "object") return ids;

    if (Array.isArray(value)) {
        value.forEach((item) => collectFlowIds(item, ids));
        return ids;
    }

    Object.entries(value).forEach(([key, nestedValue]) => {
        if (key === "id" && typeof nestedValue === "string" && nestedValue) {
            ids.push(nestedValue);
        } else if (key !== "data") {
            collectFlowIds(nestedValue, ids);
        }
    });

    return ids;
};

const mergeCollection = (current = [], generated = []) => {
    const merged = [...(Array.isArray(current) ? current : [])];
    const indexes = new Map();

    merged.forEach((item, index) => {
        const key = item?.id || item?.name;
        if (key) indexes.set(key, index);
    });

    (Array.isArray(generated) ? generated : []).forEach((item) => {
        const key = item?.id || item?.name;
        if (!key) {
            merged.push(item);
            return;
        }

        if (indexes.has(key)) {
            merged[indexes.get(key)] = {...merged[indexes.get(key)], ...item};
        } else {
            indexes.set(key, merged.length);
            merged.push(item);
        }
    });

    return merged;
};

const mergeNamedCollection = (current = [], generated = []) => {
    const merged = [...(Array.isArray(current) ? current : [])];
    const indexes = new Map();

    merged.forEach((item, index) => {
        if (item?.name) indexes.set(item.name, index);
    });

    (Array.isArray(generated) ? generated : []).forEach((item) => {
        if (!item?.name) {
            merged.push(item);
            return;
        }

        if (indexes.has(item.name)) {
            const index = indexes.get(item.name);
            merged[index] = {...merged[index], ...item};
        } else {
            indexes.set(item.name, merged.length);
            merged.push(item);
        }
    });

    return merged;
};

const toCollection = (value) => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object") return Object.values(value);
    return [];
};

const mergeRecord = (current = {}, generated = {}) => ({
    ...(current && typeof current === "object" && !Array.isArray(current) ? current : {}),
    ...(generated && typeof generated === "object" && !Array.isArray(generated) ? generated : {}),
});

const mergeGeneratedProject = (project, responseData, llmModel) => {
    const generatedRoot = responseData?.project || responseData?.data?.project || responseData?.data || responseData;
    const generatedFlow = generatedRoot?.flow
        || responseData?.flow
        || (Array.isArray(responseData?.nodes) ? responseData : null);
    const generated = generatedRoot && typeof generatedRoot === "object" ? generatedRoot : {};
    const mergedProject = {...project};
    const resourceSources = generatedFlow && generatedFlow !== generated
        ? [generated, generatedFlow]
        : [generated];

    ["intents", "entities", "constants", "variables"].forEach((key) => {
        const generatedValues = resourceSources
            .map((source) => source?.[key])
            .filter((value) => value !== undefined);
        if (generatedValues.length === 0) return;

        if (key === "constants" || key === "variables") {
            mergedProject[key] = generatedValues.reduce((current, generatedValue) => (
                Array.isArray(generatedValue)
                    ? mergeCollection(current, generatedValue)
                    : mergeRecord(current, generatedValue)
            ), project[key]);
        } else {
            mergedProject[key] = generatedValues.reduce((current, generatedValue) => {
                const collection = toCollection(generatedValue).map((entity) => key === "entities"
                    ? (() => {
                        const entityType = entity?.entity_type || entity?.entityType || entity?.type || "FreeTextEntity";
                        return {
                            ...entity,
                            ...(entityType ? {
                                entity_type: entityType,
                                // Compatibility with the currently deployed Firebase mapper.
                                entityType: entityType,
                            } : {}),
                        };
                    })()
                    : entity);
                return mergeNamedCollection(current, collection);
            }, project[key]);
        }
    });

    if (generatedFlow) {
        const currentFlow = project.flows?.[0];
        const normalizedFlow = {
            ...generatedFlow,
            id: generatedFlow.id || currentFlow?.id || uuid(),
            entryNode: generatedFlow.entryNode || generatedFlow["entry-node"] || currentFlow?.entryNode,
        };

        delete normalizedFlow["entry-node"];

        const jsonEntities = (mergedProject.entities || []).filter((entity) => entity?.entity_type === "JSONSchemaEntity");
        const onlyEntity = jsonEntities.length === 1 ? jsonEntities[0] : null;
        if (Array.isArray(normalizedFlow.nodes)) {
            normalizedFlow.nodes = normalizedFlow.nodes.map((node) => {
                const isAgentNode = [NodeType.GEN_AI_WORKFLOW, NodeType.CHATBOT, NodeType.AGENT].includes(node?.type);
                let data = node?.data;
                if (llmModel && (isAgentNode || data?.model || data?.llm_model)) {
                    const {apiKey, ...llmModelRecord} = llmModel;
                    data = {...data, model: llmModel.modelId, llm_model: llmModelRecord};
                }
                if (onlyEntity?.name && isAgentNode && !data?.parameter?.entity_type) {
                    data = {
                        ...data,
                        parameter: {
                            ...data?.parameter,
                            entity_type: onlyEntity.name,
                            description: onlyEntity.system_prompt || null,
                            name: data?.parameter?.name || "",
                        },
                    };
                }
                return data === node?.data ? node : {...node, data};
            });
        }

        mergedProject.flows = mergeCollection(project.flows, [normalizedFlow]);

        if (normalizedFlow.entryNode) {
            mergedProject.entryNode = normalizedFlow.entryNode;
        }
    }

    return mergedProject;
};

export const OnboardingPage = () => {

    const {currentUser} = useContext(SignInContext);
    const {createProject, updateProject, publishVersion} = useContext(AppContext);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const started = useRef(false);
    const [step, setStep] = useState(0);
    const [error, setError] = useState("");
    const [messageIndex, setMessageIndex] = useState(0);

    useEffect(() => {
        if (step !== GENERATION_STEP || error) return;
        setMessageIndex(0);
        const intervalId = setInterval(
            () => setMessageIndex((index) => (index + 1) % GENERATION_MESSAGES.length),
            GENERATION_MESSAGE_INTERVAL_MS
        );
        return () => clearInterval(intervalId);
    }, [step, error]);

    useEffect(() => {
        if (!currentUser || started.current) return;

        const prompt = (searchParams.get("prompt") || "").trim();
        const languageCode = searchParams.get("language");
        const siteUrl = searchParams.get("site_url") || "";
        if (!prompt || !languageCode) {
            navigate("/projects", {replace: true});
            return;
        }

        started.current = true;

        const runOnboarding = async () => {
            let failedOperation = "setup";
            // set once the backend project / its Firebase flow exist, so a failed creation can be rolled back
            let createdProjectId = null;
            let createdFlowUid = null;
            try {
                const languagesResponse = await axios.get("/api/language-support/all");
                const language = (Array.isArray(languagesResponse?.data) ? languagesResponse.data : [])
                    .find((item) => item.code === languageCode);

                if (!language?.id || !language?.code || !language?.lang) {
                    throw new Error("No valid language was selected.");
                }
                const redirect_message =
                    REDIRECT_MESSAGES_BY_LANGUAGE[language.lang.split("-")[0].toLowerCase()] ||
                    REDIRECT_MESSAGES_BY_LANGUAGE.en;
                const ownerName = [currentUser.name, currentUser.lastname || currentUser.lastName]
                    .filter(Boolean)
                    .join(" ") || "My";
                failedOperation = "organization";

                setStep(1);
                const organizationResponse = await OrganizationRepository.create({
                    name: `${ownerName}'s Organization`,
                    type: "CUSTOMER",
                    organization_type: "CUSTOMER",
                    members: [currentUser.username],
                    active: true,
                });

                const organizationId = organizationResponse?.data?.id;
                if (!organizationId) throw new Error("Organization could not be created");

                setStep(2);
                // the backend creates the project and one sub project; the sub project's flowUid is the Firebase flow id
                failedOperation = "project";
                const projectName = `${currentUser.name}'s AI agent`;
                // a new organization has no backend configuration of its own; there is no default flag, so use the
                // configured one, else the first public one
                const backendConfigurationId = process.env.REACT_APP_ONBOARDING_BACKEND_CONFIGURATION_ID
                    || (await BackendConfigurationRepository.getPublicConfigurations().catch(() => null))?.data?.[0]?.id
                    || null;
                const projectResponse = await ProjectRepository.create({
                    name: projectName,
                    description: prompt,
                    organizationId,
                    backendConfigurationId,
                    // no name: the backend defaults it to the language's name
                    subProjects: [{name: null, description: null, languageId: language.id}],
                    type: "VOICE_ASSISTANT",
                });
                createdProjectId = projectResponse?.data?.id;
                const subProject = projectResponse?.data?.subProjects?.[0];
                const projectId = subProject?.flowUid;
                if (!createdProjectId || !projectId) throw new Error("The project was created without a sub project");
                const configuration = {
                    "mcp_registry": "https://client.mcp.zmc.openbrain.io/",
                    "max_calls_by_user": null,
                    "conversation_logger_api": "https://api.vb.zmc.openbrain.io",
                    "is_live": true,
                    "chatbot_api": "https://stage.core.zmc.openbrain.io/api/receive"
                };
                const project = {
                    ...ProjectEntity,
                    id: projectId,
                    name: projectName,
                    is_demo: true,
                    redirect_message: redirect_message,
                    description: prompt,
                    configuration: configuration,
                    team: [currentUser.username],
                    org_id: organizationId,
                    members: [{username: currentUser.username, role: ProjectRoles.ADMIN, vdn: ""}],
                    settings: {
                        ...ProjectEntity.settings,
                        speechToText: 51,
                        status: "BUILDING_SCENARIOS",
                        mode: "stage",
                        organization: organizationId,
                        language: language.id,
                        lang: language.lang,
                        org_id: organizationId,
                        projectType: "VOICE_BOT",
                        project_id: createdProjectId,
                        subproject_id: subProject.id,
                    },
                };
                failedOperation = "project";
                setStep(2);
                await createProject(project);
                createdFlowUid = projectId;
                // same as the create page: the default voice / transcribe configuration of the language, plus live
                // (Firebase already has is_live). The other fields keep their value.
                const transcribeModels = (await TranscribeModelRepository.getMyModels().catch(() => null))?.data;
                await SubProjectRepository.edit(subProject.id, {
                    ...toApiConfiguration(createLanguageConfig(language, transcribeModels)),
                    live: true,
                }).catch(e => console.error("Configuring the sub project failed:", e));
                setStep(3);
                const siteMarkdown = await fetchSiteKnowledge(siteUrl);
                if (siteMarkdown) {
                    setStep(4);
                    await saveSiteKnowledge({
                        siteUrl,
                        markdown: siteMarkdown,
                        organizationId,
                        projectId: createdProjectId,
                        subProjectId: subProject.id,
                    });
                }
                const knowledgeBase = siteMarkdown.slice(0, MAX_KB_CHARS);

                failedOperation = "generation";
                setStep(5);
                const augmentedMessage = knowledgeBase
                    ? `${prompt}

## About the Business
You are building this flow for the business described below. Use this information to design a flow that fits how this business actually operates.

<business_knowledge>
${knowledgeBase}
</business_knowledge>

When designing the flow:
- Base the flow's paths on the business's real services, products, and processes (e.g. if they offer booking, include a booking path; if they list FAQs, cover those topics).
- Write node messages in a way that reflects this business, using its real names, offerings, and details instead of generic placeholders.
- Match the tone to the business (e.g. formal for a clinic, casual for a café).
- Only use facts that appear above.`
                    : prompt;

                const flowGenerationPayload = {
                    message: augmentedMessage,
                    language: language.lang,
                    known_intent_ids: (project.intents || [])
                        .map((intent) => intent?.id)
                        .filter(Boolean),
                    existing_ids: [...new Set(collectFlowIds(project.flows || []))],
                };
                let responseData;
                try {
                    responseData = await generateFlow(flowGenerationPayload);
                } catch (e) {
                    throw new Error(
                        e.response?.data?.detail ||
                        e.response?.data?.message ||
                        (e.isAxiosError
                            ? `Flow generation failed${e.response ? ` with status ${e.response.status}` : ""}.`
                            : e.message)
                    );
                }

                if (!responseData || typeof responseData !== "object") {
                    throw new Error("Flow generation returned empty or invalid data.");
                }

                const llmModels = (await LlmModelRepository.getMyModels().catch(() => null))?.data;
                const llmModel = (Array.isArray(llmModels) ? llmModels : []).find((model) => model.modelId === DEFAULT_LLM_MODEL_ID);
                if (!llmModel) console.warn(`Default LLM model ${DEFAULT_LLM_MODEL_ID} not found, keeping the generated one.`);
                const mergedProject = mergeGeneratedProject(project, responseData, llmModel);
                failedOperation = "save";
                setStep(6);
                await updateProject(mergedProject);
                failedOperation = "publish";
                await publishVersion(mergedProject);

                setStep(STEPS.length - 1);
                setTimeout(() => navigate(`/project/${projectId}/flow`, {replace: true}), 700);
            } catch (onboardingError) {
                console.error("Onboarding failed:", {
                    operation: failedOperation,
                    error: onboardingError,
                });

                // roll back so the backend project and its Firebase flow are created together or not at all;
                // later failures (generation, save, publish) keep the created project
                if (failedOperation === "project") {
                    if (createdFlowUid) {
                        await deleteDoc(doc(db, PROJECTS_COLLECTION, createdFlowUid)).catch(e => console.error(e));
                    }
                    if (createdProjectId) {
                        await ProjectRepository.delete(createdProjectId).catch(e => console.error(e));
                    }
                }

                setError(
                    FRIENDLY_ERROR_MESSAGES[failedOperation] ||
                    FRIENDLY_ERROR_MESSAGES.setup
                );
            }
        };
        runOnboarding();
    }, [currentUser, createProject, updateProject, navigate, publishVersion, searchParams]);


    const total = STEPS.length;
    const isComplete = step >= total - 1;
    const currentNumber = Math.min(step + 1, total);
    const progress = Math.round((step / (total - 1)) * 100);

    return (
        <Box className="onboarding-page">
            <Box className="onboarding-card">
                <Typography className="onboarding-eyebrow">OPENBRAIN SETUP</Typography>
                <Typography component="h1" className="onboarding-title">
                    {error ? "Something went wrong." : isComplete ? "Your agent is ready." : "Building your agent."}
                </Typography>
                <Typography className="onboarding-description">
                    {error
                        ? error
                        : isComplete
                            ? "Opening your flow editor now."
                            : "This usually takes a few minutes. You can keep this tab open."}
                </Typography>

                <Box className="onboarding-progress-head">
                    <span className="onboarding-progress-count">Step {currentNumber} of {total}</span>
                </Box>
                <Box
                    className="onboarding-progress"
                    role="progressbar"
                    aria-valuenow={progress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label="Onboarding progress"
                >
                    <Box className={`onboarding-progress-fill ${isComplete || error ? "is-still" : ""}`}
                         style={{width: `${progress}%`}}/>
                </Box>

                <Box className="onboarding-steps" role="list">
                    {STEPS.map((label, index) => {
                        const state = index < step || (isComplete && index === step)
                            ? "completed"
                            : index === step ? (error ? "failed" : "active") : "upcoming";
                        return (
                            <Box key={label} className={`onboarding-step ${state}`} role="listitem">
                                <Box className="onboarding-step-indicator" aria-hidden="true">
                                    {state === "completed" ? (
                                        <span className="onboarding-check">✓</span>
                                    ) : state === "active" ? (
                                        <span className="onboarding-dot"/>
                                    ) : state === "failed" ? (
                                        "!"
                                    ) : (
                                        index + 1
                                    )}
                                </Box>
                                <Box className="onboarding-step-text">
                                    <Typography className="onboarding-step-label">{label}</Typography>
                                    {state === "active" && (
                                        <Typography
                                            key={step === GENERATION_STEP ? messageIndex : step}
                                            className="onboarding-step-hint"
                                        >
                                            {step === GENERATION_STEP
                                                ? GENERATION_MESSAGES[messageIndex]
                                                : STATUS_MESSAGES[Math.min(step, STATUS_MESSAGES.length - 1)]}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                {error && (
                    <button type="button" className="onboarding-retry" onClick={() => window.location.reload()}>
                        Try again
                    </button>
                )}
            </Box>
        </Box>
    );
};

export default OnboardingPage;
